
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { FaAngleUp, FaAngleDown } from 'react-icons/fa';
import { IoChevronDown, IoSearchOutline, IoBriefcaseOutline, IoPricetagOutline, IoCheckmarkCircleOutline, IoFlagOutline, IoRefresh, IoPeopleOutline, IoCalendarOutline, IoBusinessOutline, IoDownloadOutline } from 'react-icons/io5';
import './Dashboard.css';

// --- Gantt Chart Specific Imports ---
import { Gantt, ViewMode } from 'gantt-task-react';
import 'gantt-task-react/dist/index.css';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

// --- Configuration ---
const SWAGGER_BASE_URL = process.env.REACT_APP_SWAGGER_SERVICE_BASE_URL;
const NODE_BASE_URL = process.env.REACT_APP_NODE_SERVICE_BASE_URL;

// --- CACHE UTILITIES ---
const CACHE_TTL_MINUTES = 60; // Cache duration

const getCache = (key) => {
    try {
        const itemStr = localStorage.getItem(key);
        if (!itemStr) return null;
        const item = JSON.parse(itemStr);
        const now = new Date();
        if (now.getTime() > item.expiry) {
            localStorage.removeItem(key);
            return null;
        }
        return item.value;
    } catch (e) {
        return null;
    }
};

const setCache = (key, value, ttlMinutes = CACHE_TTL_MINUTES) => {
    try {
        const now = new Date();
        const item = {
            value: value,
            expiry: now.getTime() + ttlMinutes * 60000,
        };
        localStorage.setItem(key, JSON.stringify(item));
    } catch (e) {
        console.warn("Local storage likely full, skipping cache for", key);
    }
};

// --- Session Cache for heavy task data (clears on tab close) ---
const getSessionCache = (key) => {
    try {
        const item = sessionStorage.getItem(key);
        return item ? JSON.parse(item) : null;
    } catch { return null; }
};
const setSessionCache = (key, value) => {
    try {
        sessionStorage.setItem(key, JSON.stringify(value));
    } catch { console.warn("Session storage full"); }
};


// --- Helper function to filter hierarchy for active users ---
const filterActiveUsersInHierarchy = (nodes) => {
    if (!nodes || !Array.isArray(nodes)) return [];
    return nodes
        .filter(node => node.active === true) // Keep only nodes where active is true
        .map(node => ({
            ...node,
            // Recursively filter the children of the active node
            children: filterActiveUsersInHierarchy(node.children)
        }));
};

// --- Gantt Chart Helper Functions & Components ---
const formatDateDDMMYY = (date) => {
    const d = date instanceof Date ? date : new Date(date);
    if (isNaN(d.getTime())) return "N/A";
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = String(d.getFullYear()).slice(-2);
    return `${day}/${month}/${year}`;
};

const getProgress = (status) => {
    if (!status) return 0;
    const lower = String(status).toLowerCase();
    if (lower.includes('completed')) return 100;
    if (lower.includes('review')) return 75;
    if (lower.includes('wip') || lower.includes('in-progress')) return 50;
    if (lower.includes('open') || lower.includes('raised')) return 10;
    return 0;
};

const formatTotalDuration = (start, end) => {
    const startDate = start instanceof Date ? start : new Date(start);
    const endDate = end instanceof Date ? end : new Date(end);
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return "N/A";
    const diffMs = endDate.getTime() - startDate.getTime();
    if (diffMs < 0) return "0d";
    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    return `${days}d`;
};

const CustomTaskListHeader = ({ headerHeight }) => (
    <div className="gantt-task-list-header" style={{ height: headerHeight, display: 'flex', alignItems: 'center', fontWeight: 'bold', borderBottom: '1px solid #e0e0e0' }}>
        <div style={{ flex: '0 0 50%', padding: '0.5rem' }}>Name</div>
        <div style={{ flex: '0 0 25%', padding: '0.5rem', borderLeft: '1px solid #e0e0e0', textAlign: 'center' }}>Start</div>
        <div style={{ flex: '0 0 25%', padding: '0.5rem', borderLeft: '1px solid #e0e0e0', textAlign: 'center' }}>End</div>
    </div>
);

const CustomTaskListTable = ({ tasks, rowHeight, onToggleProject, expandedProjects, onToggleTask, expandedTasks }) => (
    <div className="gantt-task-list-table">
        {tasks.map((item) => {
            const isProject = item.type === 'project';
            const isTaskWithSubtasks = item.type === 'task' && item.hasSubtasks;
            const isSubtask = item.type === 'subtask';

            const isProjectExpanded = isProject && expandedProjects.has(item.id);
            const isTaskExpanded = isTaskWithSubtasks && expandedTasks.has(item.id);

            return (
                <div key={item.id} className="gantt-task-list-row" style={{ height: rowHeight, display: 'flex', alignItems: 'center', borderBottom: '1px solid #efefef', background: isProject ? '#f9fafb' : 'white' }}>
                    <div className="task-list-name-column" style={{ flex: '0 0 50%' }}>
                        {isProject && <button onClick={() => onToggleProject(item.id)} className="expand-button">{isProjectExpanded ? '[-]' : '[+]'}</button>}
                        {isTaskWithSubtasks && <button onClick={() => onToggleTask(item.id, item.hasSubtasks)} className="expand-button" style={{ marginLeft: '1rem' }}>{isTaskExpanded ? '[-]' : '[+]'}</button>}
                        {isSubtask && <div style={{ width: '3.5rem', flexShrink: 0, display: 'inline-block' }}></div>}
                        {!isProject && !isTaskWithSubtasks && !isSubtask && <div style={{ width: '2rem', flexShrink: 0 }}></div>}

                        <span style={{ fontWeight: isProject ? '600' : '400' }}>{item.displayName}</span>
                    </div>
                    <div style={{ flex: '0 0 25%', borderLeft: '1px solid #e0e0e0', textAlign: 'center', padding: '0 5px' }}>{formatDateDDMMYY(item.start)}</div>
                    <div style={{ flex: '0 0 25%', borderLeft: '1px solid #e0e0e0', textAlign: 'center', padding: '0 5px' }}>{formatDateDDMMYY(item.end)}</div>
                </div>
            );
        })}
    </div>
);
// --- End Gantt Chart Helper Functions & Components ---


// --- Helper Component for the Tree (Recursive) ---
const TreeNode = ({ node, depth = 0, onClick, onTaskClick }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const hasChildren = node.children && node.children.length > 0;
    const hasTasks = node.tasks && node.tasks.length > 0;
    const isExpandable = hasChildren || hasTasks;
    const isCardClickable = onClick && hasTasks;

    const handleToggle = (e) => {
        e.stopPropagation();
        setIsExpanded(!isExpanded);
    };

    const styleIndex = depth % 5;
    const avatarInitial = node.name ? node.name.charAt(0).toUpperCase() : '?';

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            return date.toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false, }).replace(',', '');
        } catch { return dateString; }
    };

    const getStatusInfo = (status) => {
        const lowerStatus = String(status || '').toLowerCase();
        const colors = { color: '#6B7280', bgColor: '#F3F4F6' };
        if (lowerStatus.includes('completed')) { colors.color = '#10B981'; colors.bgColor = '#ECFDF5'; }
        else if (lowerStatus.includes('review')) { colors.color = '#F59E0B'; colors.bgColor = '#FFFBEB'; }
        else if (lowerStatus.includes('follow')) { colors.color = '#8B5CF6'; colors.bgColor = '#F3E8FF'; }
        else if (lowerStatus.includes('open')) { colors.color = '#3B82F6'; colors.bgColor = '#EFF6FF'; }
        else if (lowerStatus.includes('wip') || lowerStatus.includes('in-progress')) { colors.color = '#EC4899'; colors.bgColor = '#FDF2F8'; }
        return colors;
    };

    const getPriorityColor = (priority) => {
        if (!priority) return '#6B7280';
        const lowerPriority = priority.toLowerCase();
        if (lowerPriority === 'high') return '#EF4444';
        if (lowerPriority === 'medium') return '#F59E0B';
        if (lowerPriority === 'low') return '#10B981';
        return '#6B7280';
    };

    return (
        <li className="tree-node">
            <div className={`node-card style-${styleIndex} ${isCardClickable ? 'clickable' : ''}`} style={node.bgColor ? { backgroundColor: node.bgColor } : {}} onClick={isCardClickable ? onClick : undefined} >
                <div className="node-avatar-wrapper" style={node.bgColor && node.color ? { backgroundColor: node.bgColor, color: node.color } : {}}>
                    {avatarInitial}
                </div>
                <div className="node-details">
                    <h3 className="node-title">{node.name || 'Unnamed'}</h3>
                    <p className="node-designation">{node.designation || 'No Details'}</p>
                </div>
                {isExpandable && (<button onClick={handleToggle} className="toggle-button" title={isExpanded ? 'Collapse' : 'Expand'}>{isExpanded ? <FaAngleUp /> : <FaAngleDown />}</button>)}
            </div>
            {isExpanded && hasTasks && (
                <div className="tasks-wrapper">
                    <ul className="tasks-list">
                        {node.tasks.map((task) => {
                            const statusColors = getStatusInfo(task.status);
                            const priorityColor = getPriorityColor(task.priority);
                            return (
                                <li key={task.taskId} className="task-item clickable" onClick={() => onTaskClick && onTaskClick(task.taskId)}>
                                    <div className="task-details">
                                        <strong>{task.title}</strong>
                                        <p>Customer: {task.customerName || 'N/A'}</p>
                                        <p style={{ color: statusColors.color, backgroundColor: statusColors.bgColor, padding: '0.25rem 0.5rem', borderRadius: '0.25rem' }}>Status: {task.status}</p>
                                        <p style={{ color: priorityColor, backgroundColor: '#F3F4F6', padding: '0.25rem 0.5rem', borderRadius: '0.25rem' }}>Priority: {task.priority}</p>
                                        <p>Due Date: {formatDate(task.dueDate)}</p>
                                        <p>Project: {task.projectName || 'N/A'}</p>
                                        {task.description && <p className="description">Description: {task.description}</p>}
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            )}
            {hasChildren && isExpanded && (
                <div className="children-wrapper expanded">
                    <ul className="children-container">
                        {node.children.map((child) => (<TreeNode key={child.id} node={child} depth={depth + 1} onTaskClick={onTaskClick} />))}
                    </ul>
                </div>
            )}
        </li>
    );
};

// --- Reusable Multi-Select Dropdown Component ---
const MultiSelectDropdown = ({ options = [], value, onChange, icon, defaultLabel, displayAllSelectedLabels = false }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const wrapperRef = useRef(null);
    const hoverTimeout = useRef(null);

    useEffect(() => {
        function handleClickOutside(event) {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsOpen(false);
                setSearchTerm('');
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [wrapperRef]);

    const handleToggleOption = (optionValue) => { onChange(optionValue); };
    const handleMouseEnter = () => { 
        clearTimeout(hoverTimeout.current); 
        // Only open if there are options available
        if (options && options.length > 0) {
            setIsOpen(true); 
        }
    };
    const handleMouseLeave = () => { hoverTimeout.current = setTimeout(() => { setIsOpen(false); setSearchTerm(''); }, 300); };

    const getTriggerLabel = () => {
        if (!value || value.length === 0) {
            return defaultLabel;
        }

        if (displayAllSelectedLabels) {
            const selectedLabels = value
                .map(v => {
                    const option = options.find(opt => opt.value === v);
                    return option ? option.label : null;
                })
                .filter(Boolean);

            if (selectedLabels.length > 0) {
                return selectedLabels.join(', ');
            }
            return defaultLabel;
        }

        if (value.length === 1) {
            const selected = options.find(opt => opt.value === value[0]);
            return selected ? selected.label : defaultLabel;
        }

        return `${value.length} Selected`;
    };

    const filteredOptions = options.filter(option => option.label.toLowerCase().includes(searchTerm.toLowerCase()));

    return (
        <div className="custom-select-wrapper" ref={wrapperRef} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
            <div className="custom-select-trigger">
                {icon}
                <span>{getTriggerLabel()}</span>
                <IoChevronDown className={`arrow ${isOpen ? 'open' : ''}`} />
            </div>
            {isOpen && (
                <div className="custom-options">
                    <div className="custom-options-search">
                        <IoSearchOutline />
                        <input type="text" placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} autoFocus onClick={e => e.stopPropagation()} />
                    </div>
                    {filteredOptions.length > 0 ? (
                        filteredOptions.map(option => (
                            <div key={option.value} className={`custom-option ${value.includes(option.value) ? 'selected' : ''}`} onClick={() => handleToggleOption(option.value)}>
                                <input type="checkbox" checked={value.includes(option.value)} readOnly />
                                <label>{option.label}</label>
                            </div>
                        ))
                    ) : (
                        <div className="custom-option-message">
                            {searchTerm ? 'No results found' : (options.length === 0 ? 'No data available' : 'No results found')}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};


// --- Main Dashboard Component ---
const Dashboard = () => {
    const navigate = useNavigate();
    const [userDetails, setUserDetails] = useState(null);
    const [isDashboardLoading, setIsDashboardLoading] = useState(true);
    const [dashboardError, setDashboardError] = useState(null);

    // Master Data State
    const [allAvailableTasks, setAllAvailableTasks] = useState([]);
    const [statusLabels, setStatusLabels] = useState({});
    const [masterMonths, setMasterMonths] = useState([]);
    const [masterYears, setMasterYears] = useState([]);
    const [masterVendorTypes, setMasterVendorTypes] = useState([]);
    const [masterCustomers, setMasterCustomers] = useState([]);
    const [masterSuppliers, setMasterSuppliers] = useState([]);
    const [teamUsers, setTeamUsers] = useState([]);
    const [treeData, setTreeData] = useState([]);
    const [isTreeLoading, setIsTreeLoading] = useState(true);
    const [treeError, setTreeError] = useState(null);

    // Filter State
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [selectedDepartments, setSelectedDepartments] = useState([]);
    const [selectedVendorTypes, setSelectedVendorTypes] = useState([]);
    const [selectedCustomers, setSelectedCustomers] = useState([]);
    const [selectedSuppliers, setSelectedSuppliers] = useState([]);
    const [selectedYears, setSelectedYears] = useState([]);
    const [selectedMonths, setSelectedMonths] = useState([]);
    const [selectedProjects, setSelectedProjects] = useState([]);
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [selectedStatuses, setSelectedStatuses] = useState([]);
    const [selectedPriorities, setSelectedPriorities] = useState([]);

    // Derived State (for legacy/general use)
    const [filteredTasks, setFilteredTasks] = useState([]);

    // --- Specific API Implementations ---
    const [rawUserApiTasks, setRawUserApiTasks] = useState([]); // Raw data from get-task-by-userId
    const [isLoadingUserApiTasks, setIsLoadingUserApiTasks] = useState(false);
    const userApiTasksCache = useRef({}); // Memory cache for tasks by user

    const [filteredUserApiTasks, setFilteredUserApiTasks] = useState([]); // Filtered data for Project/Category/Status/User

    // Derived Stats from filteredUserApiTasks
    const [projectTaskStats, setProjectTaskStats] = useState([]);
    const [categoryTaskStats, setCategoryTaskStats] = useState([]);
    const [userWiseTaskStats, setUserWiseTaskStats] = useState([]);


    // Gantt Chart State
    const [ganttViewMode, setGanttViewMode] = useState(ViewMode.Week);
    const [expandedProjects, setExpandedProjects] = useState(new Set());
    const [expandedTasks, setExpandedTasks] = useState(new Set());
    const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
    const ganttChartRef = useRef(null);

    // --- State for dynamically fetched subtasks ---
    const [subtaskCache, setSubtaskCache] = useState({});
    const [loadingSubtasks, setLoadingSubtasks] = useState(new Set());

    const API_TOKEN = 'Bearer ' + localStorage.getItem('zTaskAuthToken');

    const customerVendorTypeId = useMemo(() => masterVendorTypes.find(vt => vt.value?.toLowerCase() === 'customer')?.id, [masterVendorTypes]);
    const supplierVendorTypeId = useMemo(() => masterVendorTypes.find(vt => vt.value?.toLowerCase() === 'supplier')?.id, [masterVendorTypes]);

    const handleNavigateToTasks = useCallback((filter) => {
        navigate('/tasks', { state: { initialFilter: filter } });
    }, [navigate]);

    const handleTaskClick = useCallback((taskId) => {
        navigate('/tasks', { state: { initialFilter: { taskId: taskId } } });
    }, [navigate]);

    // FIX: Map internal keys to display labels that the task page expects
    const statusKeyToFilterValue = {
        open: 'Open',
        completed: 'Completed',
        wip: 'WIP',
        underreview: 'Under Review',
        followup: 'Follow up'
    };

    // --- Helper to get currently selected filters for navigation ---
    const getDashboardDrillDownFilters = () => {
        const filters = {};

        // Users: Map selected emails back to user IDs
        if (selectedUsers.length > 0) {
            filters.userId = teamUsers
                .filter(u => selectedUsers.includes(u.value))
                .map(u => String(u.id));
        }

        // Departments
        if (selectedDepartments.length > 0) {
            filters.department = selectedDepartments;
        }

        // Vendor Types
        if (selectedVendorTypes.length > 0) {
            filters.vendorTypes = selectedVendorTypes;
        }

        // Customers
        if (selectedCustomers.length > 0) {
            filters.customers = selectedCustomers.map(String);
        }

        // Suppliers
        if (selectedSuppliers.length > 0) {
            filters.suppliers = selectedSuppliers.map(String);
        }

        // Years: Map ID to label (e.g., "2023-24") as TasksPage expects label for matching
        if (selectedYears.length > 0) {
            filters.year = masterYears
                .filter(y => selectedYears.includes(y.id))
                .map(y => y.label);
        }

        // Months: Pass values directly
        if (selectedMonths.length > 0) {
            filters.month = selectedMonths;
        }

        // Projects
        if (selectedProjects.length > 0) {
            filters.projectName = selectedProjects;
        }

        // Categories
        if (selectedCategories.length > 0) {
            filters.category = selectedCategories;
        }

        // Statuses
        if (selectedStatuses.length > 0) {
            filters.status = selectedStatuses;
        }

        // Priorities
        if (selectedPriorities.length > 0) {
            filters.priority = selectedPriorities;
        }

        return filters;
    };

    // --- Helper to collect users with ID for API calls ---
    const collectAllUsers = (node, userList = []) => {
        if (node) {
            userList.push({ value: node.email, label: node.name || 'Unnamed', id: node.id });
            if (node.children) {
                node.children.forEach(child => collectAllUsers(child, userList));
            }
        }
        return userList;
    };

    // --- Initial Data Loading Effect ---
    useEffect(() => {
        const fetchAllTasks = async (apiToken) => {
            // Check session cache for tasks first (Huge performance gain on reload)
            const cachedTasks = getSessionCache('dashboard_allTasks');
            if (cachedTasks) return cachedTasks;

            let allTasks = [];
            let page = 1;
            const size = 100;
            while (true) {
                try {
                    const response = await fetch(`${SWAGGER_BASE_URL}/api/get-task?page=${page}&size=${size}`, {
                        headers: { accept: 'application/json', Authorization: apiToken }
                    });
                    if (!response.ok) {
                        console.error(`Failed to fetch tasks page ${page}: ${response.status}`);
                        break;
                    }
                    const tasks = await response.json();
                    if (tasks.length === 0) {
                        break;
                    }
                    allTasks = allTasks.concat(tasks);
                    page++;
                } catch (error) {
                    console.error('Error fetching tasks:', error);
                    break;
                }
            }
            // Cache the result
            setSessionCache('dashboard_allTasks', allTasks);
            return allTasks;
        };

        const fetchUserDetails = () => {
            try {
                const rawToken = localStorage.getItem('zTaskAuthToken');
                if (!rawToken) throw new Error("Authentication token not found. Please log in again.");
                const payload = JSON.parse(atob(rawToken.split('.')[1]));

                let userRole = 'USER';
                const rolesClaim = payload.claims?.roles;
                if (typeof rolesClaim === 'string') {
                    userRole = rolesClaim;
                } else if (Array.isArray(rolesClaim) && rolesClaim.length > 0) {
                    userRole = rolesClaim[0];
                }

                if (userRole.toUpperCase() === 'SUPER ADMIN' || userRole.toUpperCase() === 'SUPERADMIN') {
                    userRole = 'SUPER_ADMIN';
                }

                setUserDetails({ name: payload.sub || 'User', role: userRole });
                return { email: payload.sub, role: userRole };
            } catch (error) {
                console.error('Error decoding JWT:', error);
                setDashboardError(error.message || 'Failed to process user details.');
                setUserDetails({ name: 'Unknown User', role: 'USER' });
                return { email: 'Unknown', role: 'USER' };
            }
        };

        const loadInitialData = async () => {
            setIsDashboardLoading(true);
            const { email: currentUserEmail, role: currentUserRole } = fetchUserDetails();

            if (!API_TOKEN || API_TOKEN === 'Bearer null') {
                setDashboardError("No authentication token found. Please log in.");
                setIsDashboardLoading(false);
                return;
            }

            try {
                // --- CACHED DATA FETCHING STRATEGY ---
                let hierarchyData = getCache('dashboard_hierarchy');
                if (!hierarchyData) {
                    const res = await fetch(`${SWAGGER_BASE_URL}/api-get-full-hierarchy-tree`, { headers: { accept: '*/*', Authorization: API_TOKEN } });
                    if (res.ok) {
                        hierarchyData = await res.json();
                        setCache('dashboard_hierarchy', hierarchyData);
                    }
                }

                // Tasks are heavy, fetch in parallel with others
                const tasksPromise = fetchAllTasks(API_TOKEN);

                let statusData = getCache('dashboard_status_master');
                let statusPromise = Promise.resolve(statusData);
                if (!statusData) {
                    statusPromise = fetch(`${NODE_BASE_URL}/api/get-column/common_master/current_status`, { headers: { accept: 'application/json', Authorization: API_TOKEN } })
                        .then(res => res.ok ? res.json() : [])
                        .then(data => { setCache('dashboard_status_master', data); return data; });
                }

                let usersData = getCache('dashboard_users_master');
                let usersPromise = Promise.resolve(usersData);
                if (!usersData) {
                    usersPromise = fetch(`${SWAGGER_BASE_URL}/api/get-users`, { headers: { accept: 'application/json', Authorization: API_TOKEN } })
                        .then(res => res.ok ? res.json() : { userData: [] })
                        .then(data => { setCache('dashboard_users_master', data); return data; });
                }

                let customersData = getCache('dashboard_customers_master');
                let customersPromise = Promise.resolve(customersData);
                if (!customersData) {
                    customersPromise = fetch(`${NODE_BASE_URL}/customer/api/customers/`, { headers: { accept: 'application/json', Authorization: API_TOKEN } })
                        .then(res => res.ok ? res.json() : [])
                        .then(data => { setCache('dashboard_customers_master', data); return data; });
                }

                let vendorTypesData = getCache('dashboard_vendor_types_master');
                let vendorTypesPromise = Promise.resolve(vendorTypesData);
                if (!vendorTypesData) {
                    vendorTypesPromise = fetch(`${NODE_BASE_URL}/api/get-column/common_master/vendor_type`, { headers: { accept: 'application/json', Authorization: API_TOKEN } })
                        .then(res => res.ok ? res.json() : [])
                        .then(data => { setCache('dashboard_vendor_types_master', data); return data; });
                }

                const [allTasksFromApi, fetchedStatus, fetchedUsers, fetchedCustomers, fetchedVendorTypes] = await Promise.all([
                    tasksPromise, statusPromise, usersPromise, customersPromise, vendorTypesPromise
                ]);

                const activeHierarchyTree = filterActiveUsersInHierarchy(hierarchyData?.tree || []);
                setMasterVendorTypes(fetchedVendorTypes || []);

                let customerMap = new Map();
                if (fetchedCustomers) {
                    fetchedCustomers.forEach(customer => customerMap.set(customer.id, customer));
                    const customerVendorTypeIdFromApi = fetchedVendorTypes?.find(vt => vt.value.toLowerCase() === 'customer')?.id;
                    const supplierVendorTypeIdFromApi = fetchedVendorTypes?.find(vt => vt.value.toLowerCase() === 'supplier')?.id;
                    setMasterCustomers(fetchedCustomers.filter(v => String(v.vendor_type) === String(customerVendorTypeIdFromApi)));
                    setMasterSuppliers(fetchedCustomers.filter(v => String(v.vendor_type) === String(supplierVendorTypeIdFromApi)));
                }

                let userDepartmentMap = new Map();
                if (fetchedUsers) {
                    fetchedUsers.userData.forEach(user => { if (user.email && user.department?.departmentName) { userDepartmentMap.set(user.email, user.department.departmentName); } });
                }

                if (fetchedStatus) {
                    const statusMap = {};
                    fetchedStatus.forEach((status) => { if (status.value) { const apiKey = status.value.toLowerCase().replace(/[^a-z0-9]/g, ''); statusMap[apiKey] = status.value; } });
                    setStatusLabels(statusMap);
                } else {
                    setStatusLabels({ followup: 'Follow up', completed: 'Completed', underreview: 'Under Review', open: 'Open', wip: 'WIP' });
                }

                const findUserNode = (nodes, email) => {
                    for (const node of nodes) {
                        if (node.email === email) return node;
                        if (node.children?.length) { const found = findUserNode(node.children, email); if (found) return found; }
                    }
                    return null;
                };

                const allTopLevelNodes = activeHierarchyTree;
                let nodesToProcess = [];

                if (currentUserRole === 'SUPER_ADMIN') {
                    nodesToProcess = allTopLevelNodes;
                } else {
                    const userNode = findUserNode(allTopLevelNodes, currentUserEmail);
                    nodesToProcess = userNode ? [userNode] : [];
                }

                // 1. Create a flat map of all users from the hierarchy for efficient lookup.
                const userMap = new Map();
                const flattenHierarchyForLookup = (nodes) => {
                    if (!nodes) return;
                    nodes.forEach(node => {
                        if (!userMap.has(node.id)) {
                            userMap.set(node.id, node);
                        }
                        if (node.children) {
                            flattenHierarchyForLookup(node.children);
                        }
                    });
                };
                flattenHierarchyForLookup(hierarchyData?.tree || []);

                // 2. Create the master task list. 
                const completeAndUniqueTasks = (allTasksFromApi || []).map(task => {
                    const assigneeId = task.assignee_id;
                    const userNode = assigneeId ? userMap.get(assigneeId) : null;
                    const customer = customerMap.get(task.customer_id);

                    return {
                        id: task.task_id,
                        taskId: task.task_id,
                        parentTaskId: task.parent_task_id,
                        title: task.title,
                        description: task.description,
                        status: task.status || 'unknown',
                        priority: task.priority,
                        category: task.category,
                        createdAt: task.created_at,
                        dueDate: task.due_date,
                        projectName: task.project_name,
                        customerId: task.customer_id,
                        no_of_sub_tasks: task.no_of_sub_tasks || 0,

                        customerName: customer ? customer.name : 'N/A',
                        vendorTypeId: customer ? customer.vendor_type : null,
                        assigneeId: assigneeId,
                        assigneeEmail: userNode ? userNode.email : null,
                        assigneeName: task.assignee_name || (userNode ? userNode.name : (assigneeId ? `Unknown User (ID: ${assigneeId})` : 'Unassigned')),
                        assigneeDepartment: userNode ? (userDepartmentMap.get(userNode.email) || 'Unassigned') : 'Unassigned'
                    };
                });

                // 3. Filter tasks to the current user's scope.
                const collectEmailsFromNodes = (nodes) => {
                    const emails = new Set();
                    const recurse = (nodeList) => {
                        if (!nodeList) return;
                        for (const node of nodeList) {
                            if (node.email) {
                                emails.add(node.email);
                            }
                            if (node.children && node.children.length > 0) {
                                recurse(node.children);
                            }
                        }
                    };
                    recurse(nodes);
                    return emails;
                };

                const scopedUserEmails = collectEmailsFromNodes(nodesToProcess);

                let tasksForCurrentUserScope = [];
                if (currentUserRole === 'SUPER_ADMIN') {
                    tasksForCurrentUserScope = completeAndUniqueTasks;
                } else {
                    tasksForCurrentUserScope = completeAndUniqueTasks.filter(task =>
                        task.assigneeEmail && scopedUserEmails.has(task.assigneeEmail)
                    );
                }

                setAllAvailableTasks(tasksForCurrentUserScope);

                // 4. Build visual tree
                const tasksByAssigneeId = (allTasksFromApi || []).reduce((acc, task) => {
                    const assigneeId = task.assignee_id;
                    if (assigneeId) {
                        if (!acc[assigneeId]) acc[assigneeId] = [];
                        acc[assigneeId].push(task);
                    }
                    return acc;
                }, {});

                const attachTasksToHierarchy = (nodes, tasksMap, custMap) => {
                    return nodes.map(node => {
                        const userTasks = tasksMap[node.id] || [];
                        const processedTasks = userTasks.map(task => ({
                            taskId: task.task_id,
                            title: task.title,
                            status: task.status,
                            priority: task.priority,
                            dueDate: task.due_date,
                            customerName: custMap.get(task.customer_id)?.name || 'N/A',
                            projectName: task.project_name,
                            description: task.description,
                        }));

                        const childrenWithTasks = node.children && node.children.length > 0
                            ? attachTasksToHierarchy(node.children, tasksMap, custMap)
                            : [];

                        return { ...node, tasks: processedTasks, children: childrenWithTasks };
                    });
                };

                const treeWithTasks = attachTasksToHierarchy(nodesToProcess, tasksByAssigneeId, customerMap);
                setTreeData(treeWithTasks);

                const allUsersInScope = nodesToProcess.flatMap(node => collectAllUsers(node));
                const uniqueUsersMap = new Map();
                allUsersInScope.forEach(user => {
                    if (user.value && !uniqueUsersMap.has(user.value)) {
                        uniqueUsersMap.set(user.value, user);
                    }
                });
                const uniqueUsers = Array.from(uniqueUsersMap.values());
                setTeamUsers(uniqueUsers.sort((a, b) => a.label.localeCompare(b.label)));

                const taskYears = new Set();
                const taskMonths = new Set();
                tasksForCurrentUserScope.forEach((task) => {
                    if (task.createdAt) {
                        const date = new Date(task.createdAt);
                        if (!isNaN(date.getTime())) {
                            taskYears.add(date.getFullYear());
                            taskMonths.add(date.getMonth() + 1);
                        }
                    }
                });

                // Using cached master data for months/years if available, otherwise fetch
                const monthPromise = getCache('dashboard_month_master') ? Promise.resolve(getCache('dashboard_month_master')) : fetch(`${NODE_BASE_URL}/api/get-column/common_master/month_name`, { headers: { accept: 'application/json', Authorization: API_TOKEN } }).then(r => r.json());
                const fromPromise = getCache('dashboard_from_date') ? Promise.resolve(getCache('dashboard_from_date')) : fetch(`${NODE_BASE_URL}/api/get-column/common_master/from_date`, { headers: { accept: 'application/json', Authorization: API_TOKEN } }).then(r => r.json());
                const toPromise = getCache('dashboard_to_date') ? Promise.resolve(getCache('dashboard_to_date')) : fetch(`${NODE_BASE_URL}/api/get-column/common_master/to_date`, { headers: { accept: 'application/json', Authorization: API_TOKEN } }).then(r => r.json());

                const [monthData, fromData, toData] = await Promise.all([monthPromise, fromPromise, toPromise]);

                // Cache them
                setCache('dashboard_month_master', monthData);
                setCache('dashboard_from_date', fromData);
                setCache('dashboard_to_date', toData);

                if (monthData) {
                    setMasterMonths(
                        monthData
                            .filter(m => taskMonths.has(parseInt(m.id, 10)))
                            .sort((a, b) => parseInt(a.id, 10) - parseInt(b.id, 10))
                    );
                }

                if (fromData && toData) {
                    const yearsData = fromData.map((from) => {
                        const to = toData.find((t) => t.id === from.id);
                        const fromYear = new Date(from.value).getFullYear();
                        const toYear = new Date(to.value).getFullYear();
                        return { id: from.id, label: `${fromYear}-${toYear.toString().slice(-2)}`, from_date: from.value, to_date: to.value };
                    }).filter(y => taskYears.has(new Date(y.from_date).getFullYear()))
                        .sort((a, b) => a.id - b.id);
                    setMasterYears(yearsData);
                }

            } catch (error) {
                console.error("Dashboard loading error:", error);
                setDashboardError(error.message);
            } finally {
                setIsDashboardLoading(false);
                setIsTreeLoading(false);
            }
        };

        loadInitialData();
    }, []);

    // --- Effect to fetch full task lists via API (for Project/Category/Status/User) ---
    useEffect(() => {
        const fetchTasksForVisibleUsers = async () => {
            if (!teamUsers.length) return;

            const usersToFetch = selectedUsers.length > 0
                ? teamUsers.filter(u => selectedUsers.includes(u.value))
                : teamUsers;

            const missingUsers = usersToFetch.filter(user => !userApiTasksCache.current[user.id]);

            if (missingUsers.length > 0) {
                setIsLoadingUserApiTasks(true);
            }

            const pageSize = 500; // Fetch enough tasks to perform local filtering accurately

            // Fetch customer data and vendor types to populate task details
            let customerMap = new Map();
            if (masterCustomers.length > 0) {
                masterCustomers.forEach(customer => customerMap.set(customer.id, customer));
            }
            if (masterSuppliers.length > 0) {
                masterSuppliers.forEach(supplier => customerMap.set(supplier.id, supplier));
            }

            // Get user department map
            let userDepartmentMap = new Map();
            try {
                const usersResponse = await fetch(`${SWAGGER_BASE_URL}/api/get-users`, { headers: { accept: 'application/json', Authorization: API_TOKEN } });
                if (usersResponse.ok) {
                    const usersData = await usersResponse.json();
                    usersData.userData.forEach(user => {
                        if (user.email && user.department?.departmentName) {
                            userDepartmentMap.set(user.email, user.department.departmentName);
                        }
                    });
                }
            } catch (err) {
                console.error('Failed to fetch user department data:', err);
            }

            await Promise.all(missingUsers.map(async (user) => {
                if (!user.id) return;
                try {
                    // Fetch using get-task-by-userId
                    const response = await fetch(`${SWAGGER_BASE_URL}/api/get-task-by-userId?userId=${user.id}&page=1&size=${pageSize}`, {
                        headers: { accept: 'application/json', Authorization: API_TOKEN }
                    });
                    if (response.ok) {
                        const data = await response.json();
                        const tasks = data.data || [];

                        const mappedTasks = tasks.map(t => {
                            const customer = customerMap.get(t.customer_id);
                            return {
                                ...t,
                                taskId: t.task_id,
                                projectName: t.project_name,
                                assigneeName: user.label,
                                assigneeId: user.id,
                                assigneeEmail: user.value,
                                customerName: customer ? customer.name : 'N/A',
                                customerId: t.customer_id,
                                createdAt: t.created_at,
                                dueDate: t.due_date,
                                vendorTypeId: customer ? customer.vendor_type : null,
                                assigneeDepartment: userDepartmentMap.get(user.value) || 'Unassigned'
                            };
                        });
                        userApiTasksCache.current[user.id] = mappedTasks;
                    }
                } catch (err) {
                    console.error(`Failed to fetch tasks for user ${user.label} (${user.id})`, err);
                }
            }));

            // Collect all tasks from cache for visible users
            let allFetchedTasks = [];
            usersToFetch.forEach(u => {
                if (userApiTasksCache.current[u.id]) {
                    allFetchedTasks = [...allFetchedTasks, ...userApiTasksCache.current[u.id]];
                }
            });

            // Remove duplicates
            const uniqueTasks = Array.from(new Map(allFetchedTasks.map(item => [item.taskId, item])).values());

            setRawUserApiTasks(uniqueTasks);
            setIsLoadingUserApiTasks(false);
        };

        fetchTasksForVisibleUsers();
    }, [teamUsers, selectedUsers, masterCustomers, masterSuppliers]);


    // --- Combined Filtering Logic (Both Global and User-API specific) ---
    useEffect(() => {
        // Helper to apply all dropdown filters to a specific task list
        const applyCommonFilters = (taskList) => {
            let filtered = taskList;

            // 1. Dropdown Basic filters
            if (selectedUsers.length > 0) filtered = filtered.filter(task => task.assigneeEmail && selectedUsers.includes(task.assigneeEmail));
            if (selectedDepartments.length > 0) filtered = filtered.filter(task => task.assigneeDepartment && selectedDepartments.includes(task.assigneeDepartment));
            if (selectedVendorTypes.length > 0) filtered = filtered.filter(task => task.vendorTypeId != null && selectedVendorTypes.includes(String(task.vendorTypeId)));
            const selectedVendors = [...selectedCustomers, ...selectedSuppliers];
            if (selectedVendors.length > 0) filtered = filtered.filter(task => task.customerId != null && selectedVendors.includes(task.customerId));

            // 2. Date Filters (Year and Month) - Enhanced with validation
            if (selectedYears.length > 0) {
                const selectedYearData = masterYears.filter(y => selectedYears.includes(y.id));
                
                // Validate year data exists
                if (selectedYearData.length === 0) {
                    console.warn('⚠️ Year Filter Warning: Selected year IDs do not match master year data', {
                        selectedYears,
                        availableYears: masterYears.map(y => ({ id: y.id, label: y.label }))
                    });
                } else {
                    // Apply year filter with robust validation
                    filtered = filtered.filter(task => {
                        // Skip tasks without createdAt
                        if (!task.createdAt) return false;
                        
                        // Validate task date
                        const taskDate = new Date(task.createdAt);
                        if (isNaN(taskDate.getTime())) {
                            console.warn('⚠️ Invalid task date:', task.taskId, task.createdAt);
                            return false;
                        }
                        
                        // Check if task falls within any selected year range
                        return selectedYearData.some(y => {
                            // Validate year has required date fields
                            if (!y.from_date || !y.to_date) {
                                console.warn('⚠️ Year missing date range:', y);
                                return false;
                            }
                            
                            const fromDate = new Date(y.from_date);
                            const toDate = new Date(y.to_date);
                            
                            // Validate year dates are valid
                            if (isNaN(fromDate.getTime()) || isNaN(toDate.getTime())) {
                                console.warn('⚠️ Invalid year date range:', y);
                                return false;
                            }
                            
                            // Check if task is within year range (inclusive)
                            return taskDate >= fromDate && taskDate <= toDate;
                        });
                    });
                    
                    console.log('✅ Year filter applied:', {
                        selectedYears: selectedYearData.map(y => y.label),
                        tasksAfterFilter: filtered.length
                    });
                }
            }
            if (selectedMonths.length > 0) {
                filtered = filtered.filter(task => {
                    if (!task.createdAt) return false;
                    const taskMonth = new Date(task.createdAt).getMonth() + 1;
                    return selectedMonths.map(Number).includes(taskMonth);
                });
            }

            // 3. Status/Priority/Category filters
            if (selectedProjects.length > 0) filtered = filtered.filter(task => task.projectName && selectedProjects.includes(task.projectName));
            if (selectedCategories.length > 0) filtered = filtered.filter(task => task.category && selectedCategories.includes(task.category));
            if (selectedStatuses.length > 0) filtered = filtered.filter(task => task.status && selectedStatuses.includes(task.status));
            if (selectedPriorities.length > 0) filtered = filtered.filter(task => task.priority && selectedPriorities.includes(task.priority));

            return filtered;
        }

        // Apply filters to both datasets
        const globalFiltered = applyCommonFilters(allAvailableTasks);
        const apiFiltered = applyCommonFilters(rawUserApiTasks);

        setFilteredTasks(globalFiltered);
        setFilteredUserApiTasks(apiFiltered);

        // --- Calculate Stats based on Filtered API Data (Ensures Month/Year are respected) ---
        const calculateStats = (items, key) => {
            const stats = {};
            items.forEach((item) => {
                const name = item[key] || (key === 'projectName' ? 'No project assigned' : (key === 'assigneeName' ? 'Unassigned' : 'Uncategorized'));

                if (!stats[name]) {
                    stats[name] = {
                        name,
                        id: key === 'assigneeName' ? item.assigneeId : null,
                        total: 0,
                        completed: 0, wip: 0, underreview: 0, followup: 0, open: 0
                    };
                }

                stats[name].total++;
                const lowerStatus = String(item.status || '').toLowerCase();

                if (lowerStatus.includes('completed')) stats[name].completed++;
                else if (lowerStatus.includes('wip') || lowerStatus.includes('in-progress')) stats[name].wip++;
                else if (lowerStatus.includes('review')) stats[name].underreview++;
                else if (lowerStatus.includes('follow')) stats[name].followup++;
                else if (lowerStatus.includes('open') || lowerStatus.includes('raised')) stats[name].open++;
            });
            return Object.values(stats).sort((a, b) => a.name.localeCompare(b.name));
        };

        // These stats will now correctly react to the MONTH and YEAR filters
        setUserWiseTaskStats(calculateStats(apiFiltered, 'assigneeName'));
        setProjectTaskStats(calculateStats(apiFiltered, 'projectName'));
        setCategoryTaskStats(calculateStats(apiFiltered, 'category'));

    }, [selectedUsers, selectedDepartments, selectedVendorTypes, selectedCustomers, selectedSuppliers, selectedYears, selectedMonths, selectedProjects, selectedCategories, selectedStatuses, selectedPriorities, allAvailableTasks, rawUserApiTasks, masterYears]);


    // --- Memoized Dropdown Options ---
    const dropdownOptions = useMemo(() => {
        // Debug logging for data sources
        console.log('🔍 DROPDOWN OPTIONS DEBUG:');
        console.log('  allAvailableTasks:', allAvailableTasks.length);
        console.log('  teamUsers:', teamUsers.length);
        console.log('  masterMonths:', masterMonths.length);
        console.log('  masterYears:', masterYears.length);
        console.log('  masterVendorTypes:', masterVendorTypes.length);
        console.log('  masterCustomers:', masterCustomers.length);
        console.log('  masterSuppliers:', masterSuppliers.length);
        console.log('  statusLabels:', Object.keys(statusLabels).length);

        const filtersConfig = {
            users: { state: selectedUsers, filter: (tasks, values) => tasks.filter(t => t.assigneeEmail && values.includes(t.assigneeEmail)) },
            departments: { state: selectedDepartments, filter: (tasks, values) => tasks.filter(t => t.assigneeDepartment && values.includes(t.assigneeDepartment)) },
            vendorTypes: { state: selectedVendorTypes, filter: (tasks, values) => tasks.filter(t => t.vendorTypeId != null && values.includes(String(t.vendorTypeId))) },
            vendors: { state: [...selectedCustomers, ...selectedSuppliers], filter: (tasks, values) => tasks.filter(t => t.customerId != null && values.includes(t.customerId)) },
            years: {
                state: selectedYears, filter: (tasks, values) => {
                    const selectedYearData = masterYears.filter(y => values.includes(y.id));
                    return tasks.filter(t => t.createdAt && selectedYearData.some(y => new Date(t.createdAt) >= new Date(y.from_date) && new Date(t.createdAt) <= new Date(y.to_date)));
                }
            },
            months: { state: selectedMonths, filter: (tasks, values) => tasks.filter(t => t.createdAt && values.map(Number).includes(new Date(t.createdAt).getMonth() + 1)) },
            projects: { state: selectedProjects, filter: (tasks, values) => tasks.filter(t => t.projectName && values.includes(t.projectName)) },
            categories: { state: selectedCategories, filter: (tasks, values) => tasks.filter(t => t.category && values.includes(t.category)) },
            statuses: { state: selectedStatuses, filter: (tasks, values) => tasks.filter(t => t.status && values.includes(t.status)) },
            priorities: { state: selectedPriorities, filter: (tasks, values) => tasks.filter(t => t.priority && values.includes(t.priority)) }
        };

        // Helper to get unique options from tasks
        const getUniqueOptionsFromTasks = (tasks, key) => {
            const uniqueMap = new Map();
            tasks.forEach(task => {
                const value = task[key];
                if (value != null && value !== '') {
                    const normalizedKey = String(value).toLowerCase();
                    if (!uniqueMap.has(normalizedKey)) {
                        uniqueMap.set(normalizedKey, { value, label: value });
                    }
                }
            });
            return Array.from(uniqueMap.values()).sort((a, b) => String(a.label).localeCompare(String(b.label)));
        };

        // FIXED: Use base dataset for all dropdown options to prevent cascading filter issues
        // This ensures dropdowns always show all available options regardless of other filters
        const availableUserEmails = new Set(allAvailableTasks.map(t => t.assigneeEmail).filter(Boolean));
        const userOptions = teamUsers.filter(u => availableUserEmails.has(u.value));

        const departmentOptions = getUniqueOptionsFromTasks(allAvailableTasks, 'assigneeDepartment');

        const vendorTypeOptions = masterVendorTypes
            .map(vt => ({ value: String(vt.id), label: vt.value })).sort((a, b) => a.label.localeCompare(b.label));

        const customerOptions = masterCustomers
            .map(c => ({ value: c.id, label: c.name })).sort((a, b) => a.label.localeCompare(b.label));
        const supplierOptions = masterSuppliers
            .map(s => ({ value: s.id, label: s.name })).sort((a, b) => a.label.localeCompare(b.label));

        // Year options - use base dataset
        const availableYearValues = new Set();
        allAvailableTasks.forEach(t => { 
            if (t.createdAt) availableYearValues.add(new Date(t.createdAt).getFullYear()); 
        });
        const yearOptions = masterYears.filter(y => availableYearValues.has(new Date(y.from_date).getFullYear()))
            .map(y => ({ value: y.id, label: y.label }));

        // Month options - use base dataset
        const availableMonthValues = new Set();
        allAvailableTasks.forEach(t => { 
            if (t.createdAt) availableMonthValues.add(new Date(t.createdAt).getMonth() + 1); 
        });
        const monthOptions = masterMonths.filter(m => availableMonthValues.has(parseInt(m.id, 10)))
            .map(m => ({ value: String(m.id), label: m.value }));

        // Project and Category options - use base dataset
        const projectOptions = getUniqueOptionsFromTasks(allAvailableTasks, 'projectName');
        const categoryOptions = getUniqueOptionsFromTasks(allAvailableTasks, 'category');

        const statusOptions = Object.entries(statusLabels)
            .map(([key, label]) => ({ value: key, label })).sort((a, b) => a.label.localeCompare(b.label));

        // Priority options - use base dataset
        const priorityOptions = getUniqueOptionsFromTasks(allAvailableTasks, 'priority')
            .map(p => ({ ...p, label: p.label.charAt(0).toUpperCase() + p.label.slice(1) }));

        // Debug logging for generated options
        console.log('📋 GENERATED DROPDOWN OPTIONS:');
        console.log('  userOptions:', userOptions.length);
        console.log('  departmentOptions:', departmentOptions.length);
        console.log('  vendorTypeOptions:', vendorTypeOptions.length);
        console.log('  customerOptions:', customerOptions.length);
        console.log('  supplierOptions:', supplierOptions.length);
        console.log('  yearOptions:', yearOptions.length);
        console.log('  monthOptions:', monthOptions.length);
        console.log('  projectOptions:', projectOptions.length);
        console.log('  categoryOptions:', categoryOptions.length);
        console.log('  statusOptions:', statusOptions.length);
        console.log('  priorityOptions:', priorityOptions.length);
        
        if (userOptions.length === 0) console.warn('⚠️ userOptions is empty!');
        if (departmentOptions.length === 0) console.warn('⚠️ departmentOptions is empty!');
        if (monthOptions.length === 0) console.warn('⚠️ monthOptions is empty!');

        return { userOptions, departmentOptions, vendorTypeOptions, customerOptions, supplierOptions, projectOptions, categoryOptions, statusOptions, priorityOptions, yearOptions, monthOptions };
    }, [
        allAvailableTasks, selectedUsers, selectedDepartments, selectedVendorTypes, selectedCustomers,
        selectedSuppliers, selectedYears, selectedMonths, selectedProjects, selectedCategories,
        selectedStatuses, selectedPriorities, teamUsers, masterVendorTypes, masterCustomers, masterSuppliers,
        masterYears, masterMonths, statusLabels
    ]);


    const treeForDisplay = useMemo(() => {
        const filteredTaskIds = new Set(filteredTasks.map(t => t.id));

        const filterTasksInNode = (nodes) => {
            if (!nodes) return [];
            return nodes.map(node => ({
                ...node,
                tasks: (node.tasks || []).filter(task => filteredTaskIds.has(task.taskId)),
                children: (node.children || []).length > 0 ? filterTasksInNode(node.children) : []
            }));
        };

        const filterAndPruneNodes = (nodes) => {
            if (!nodes) return [];
            return nodes.map(node => {
                const newTasks = (node.tasks || []).filter(task => filteredTaskIds.has(task.taskId));
                const newChildren = (node.children || []).length > 0 ? filterAndPruneNodes(node.children) : [];
                const hasVisibleContent = newTasks.length > 0 || newChildren.some(child => child.hasVisibleContent);
                return { ...node, tasks: newTasks, children: newChildren, hasVisibleContent };
            }).filter(node => node.hasVisibleContent);
        };

        if (userDetails?.role === 'SUPER_ADMIN') {
            return filterTasksInNode(treeData);
        } else {
            return filterAndPruneNodes(treeData);
        }

    }, [treeData, filteredTasks, userDetails]);


    // --- Gantt chart data preparation ---
    const ganttTasks = useMemo(() => {
        const finalGanttItems = [];
        const projectsMap = new Map();

        filteredTasks.forEach(task => {
            if (!task.createdAt || !task.dueDate) return;
            if (!task.parentTaskId || task.parentTaskId === 0) {
                const projectNameKey = task.projectName || "No project assigned";
                if (!projectsMap.has(projectNameKey)) {
                    projectsMap.set(projectNameKey, { id: `project-${projectNameKey}`, displayName: projectNameKey, tasks: [], type: 'project', styles: { progressColor: '#3b82f6', progressSelectedColor: '#3b82f6' } });
                }
                projectsMap.get(projectNameKey).tasks.push(task);
            }
        });

        const sortedProjects = Array.from(projectsMap.values()).sort((a, b) => a.displayName.localeCompare(b.displayName));

        sortedProjects.forEach(project => {
            let earliestStart = null, latestEnd = null;
            let completedTasks = 0;

            project.tasks.forEach(task => {
                const start = new Date(task.createdAt), end = new Date(task.dueDate);
                if (!isNaN(start.getTime())) { if (!earliestStart || start < earliestStart) earliestStart = start; }
                if (!isNaN(end.getTime())) { if (!latestEnd || end > latestEnd) latestEnd = end; }
                if (String(task.status).toLowerCase().includes('completed')) completedTasks++;
            });

            if (!earliestStart || !latestEnd) return;
            if (latestEnd < earliestStart) latestEnd = earliestStart;
            project.start = earliestStart;
            project.end = latestEnd;
            project.progress = project.tasks.length > 0 ? Math.round((completedTasks / project.tasks.length) * 100) : 0;

            finalGanttItems.push(project);

            if (expandedProjects.has(project.id)) {
                project.tasks.forEach(task => {
                    let start = new Date(task.createdAt), end = new Date(task.dueDate);
                    if (isNaN(start.getTime()) || isNaN(end.getTime())) return;
                    if (end < start) end = start;

                    const hasSubtasks = task.no_of_sub_tasks > 0;
                    const ganttTaskId = `task-${task.taskId}`;
                    finalGanttItems.push({
                        id: ganttTaskId,
                        displayName: task.title,
                        start,
                        end,
                        progress: getProgress(task.status),
                        type: 'task',
                        project: project.id,
                        hasSubtasks,
                        styles: { progressColor: '#10b981', progressSelectedColor: '#10b981' }
                    });

                    if (hasSubtasks && expandedTasks.has(ganttTaskId)) {
                        const cachedSubtasks = subtaskCache[task.taskId];
                        if (cachedSubtasks) {
                            cachedSubtasks.forEach(subtask => {
                                let subStart = new Date(subtask.createdAt), subEnd = new Date(subtask.dueDate);
                                if (isNaN(subStart.getTime()) || isNaN(subEnd.getTime())) return;
                                if (subEnd < subStart) subEnd = subStart;
                                finalGanttItems.push({
                                    id: `subtask-${subtask.taskId}`,
                                    displayName: subtask.title,
                                    start: subStart,
                                    end: subEnd,
                                    progress: getProgress(subtask.status),
                                    type: 'subtask',
                                    project: project.id,
                                    dependencies: [ganttTaskId],
                                    styles: { progressColor: '#f59e0b', progressSelectedColor: '#f59e0b' }
                                });
                            });
                        }
                        if (loadingSubtasks.has(task.taskId)) {
                            finalGanttItems.push({
                                id: `loading-${task.taskId}`,
                                displayName: 'Loading subtasks...',
                                start: end,
                                end: end,
                                type: 'subtask',
                                project: project.id,
                                isDisabled: true,
                                styles: { backgroundColor: '#f0f0f0', progressColor: '#f0f0f0' }
                            });
                        }
                    }
                });
            }
        });

        return finalGanttItems;
    }, [filteredTasks, expandedProjects, expandedTasks, subtaskCache, loadingSubtasks]);

    const ganttKey = useMemo(() => {
        return [...expandedProjects].join(',') + '-' + [...expandedTasks].join(',');
    }, [expandedProjects, expandedTasks]);

    const handleMultiSelectChange = (setter) => (value) => {
        setter(prev => {
            const newValues = new Set(prev);
            if (newValues.has(value)) {
                newValues.delete(value);
            } else {
                newValues.add(value);
            }
            return [...newValues];
        });
    };

    const handleUsersChange = handleMultiSelectChange(setSelectedUsers);
    const handleDepartmentsChange = handleMultiSelectChange(setSelectedDepartments);
    const handleVendorTypesChange = handleMultiSelectChange(setSelectedVendorTypes);
    const handleCustomersChange = handleMultiSelectChange(setSelectedCustomers);
    const handleSuppliersChange = handleMultiSelectChange(setSelectedSuppliers);
    const handleYearsChange = handleMultiSelectChange(setSelectedYears);
    const handleMonthsChange = handleMultiSelectChange(setSelectedMonths);
    const handleProjectsChange = handleMultiSelectChange(setSelectedProjects);
    const handleCategoriesChange = handleMultiSelectChange(setSelectedCategories);
    const handleStatusChange = handleMultiSelectChange(setSelectedStatuses);
    const handlePriorityChange = handleMultiSelectChange(setSelectedPriorities);

    useEffect(() => {
        setSelectedProjects([]);
        setSelectedCategories([]);
        setSelectedStatuses([]);
        setSelectedPriorities([]);
    }, [selectedUsers, selectedDepartments, selectedVendorTypes, selectedCustomers, selectedSuppliers]);

    const handleResetFilters = () => {
        setSelectedUsers([]);
        setSelectedDepartments([]);
        setSelectedVendorTypes([]);
        setSelectedCustomers([]);
        setSelectedSuppliers([]);
        setSelectedYears([]);
        setSelectedMonths([]);
        setSelectedProjects([]);
        setSelectedCategories([]);
        setSelectedStatuses([]);
        setSelectedPriorities([]);
    };

    const handleExportCSV = () => {
        // Use filteredUserApiTasks for consistency with dashboard stats display
        // Fall back to filteredTasks only if filteredUserApiTasks is empty
        const tasksToExport = filteredUserApiTasks.length > 0 ? filteredUserApiTasks : filteredTasks;
        
        if (tasksToExport.length === 0) {
            alert("No data to export. Please adjust your filters or check if tasks are available."); 
            return;
        }

        // Get selected year data for filename and validation
        const selectedYearData = selectedYears.length > 0 
            ? masterYears.filter(y => selectedYears.includes(y.id))
            : [];

        // Validate year filter if selected
        if (selectedYears.length > 0 && selectedYearData.length === 0) {
            console.error('❌ Year filter mismatch:', {
                selectedYearIds: selectedYears,
                availableYears: masterYears.map(y => ({ id: y.id, label: y.label }))
            });
            alert('Warning: Selected year filter may not be applied correctly. Please refresh and try again.');
        }

        // Build dynamic filename with filter information
        let filename = 'dashboard_tasks';
        
        // Add year to filename
        if (selectedYearData.length > 0) {
            const yearLabels = selectedYearData
                .map(y => y.label || 'Unknown')
                .filter(Boolean)
                .join('_');
            if (yearLabels) {
                filename += `_${yearLabels}`;
            }
        } else if (selectedYears.length === 0) {
            filename += '_all_years';
        }
        
        // Add month to filename if applicable
        if (selectedMonths.length > 0) {
            const selectedMonthData = masterMonths.filter(m => selectedMonths.includes(m.id));
            const monthNames = selectedMonthData
                .map(m => m.label || m.name || 'Unknown')
                .filter(Boolean)
                .join('_');
            if (monthNames) {
                filename += `_${monthNames}`;
            }
        }
        
        // Add timestamp
        const timestamp = new Date().toISOString().split('T')[0];
        filename += `_${timestamp}.csv`;

        // Sanitize filename (remove invalid characters)
        filename = filename.replace(/[^a-zA-Z0-9_\-\.]/g, '_');

        // ===== COMPREHENSIVE DEBUG LOGGING =====
        console.log('╔════════════════════════════════════════════════════════════╗');
        console.log('║           DASHBOARD EXPORT - DEBUG INFORMATION             ║');
        console.log('╚════════════════════════════════════════════════════════════╝');
        console.log('');
        console.log('📊 ACTIVE FILTERS:');
        console.log('  └─ Users:', selectedUsers.length > 0 ? `${selectedUsers.length} selected` : 'ALL');
        console.log('  └─ Departments:', selectedDepartments.length > 0 ? `${selectedDepartments.length} selected` : 'ALL');
        console.log('  └─ Vendor Types:', selectedVendorTypes.length > 0 ? `${selectedVendorTypes.length} selected` : 'ALL');
        console.log('  └─ Customers:', selectedCustomers.length > 0 ? `${selectedCustomers.length} selected` : 'ALL');
        console.log('  └─ Suppliers:', selectedSuppliers.length > 0 ? `${selectedSuppliers.length} selected` : 'ALL');
        console.log('  └─ Years:', selectedYears.length > 0 ? selectedYearData.map(y => y.label).join(', ') : 'ALL');
        console.log('  └─ Months:', selectedMonths.length > 0 ? `${selectedMonths.length} selected` : 'ALL');
        console.log('  └─ Projects:', selectedProjects.length > 0 ? `${selectedProjects.length} selected` : 'ALL');
        console.log('  └─ Categories:', selectedCategories.length > 0 ? `${selectedCategories.length} selected` : 'ALL');
        console.log('  └─ Statuses:', selectedStatuses.length > 0 ? `${selectedStatuses.length} selected` : 'ALL');
        console.log('  └─ Priorities:', selectedPriorities.length > 0 ? `${selectedPriorities.length} selected` : 'ALL');
        console.log('');
        console.log('📅 YEAR FILTER DETAILS:');
        if (selectedYearData.length > 0) {
            selectedYearData.forEach(y => {
                console.log(`  └─ ${y.label}: ${y.from_date} to ${y.to_date}`);
            });
        } else {
            console.log('  └─ No year filter applied');
        }
        console.log('');
        console.log('📈 EXPORT DATA:');
        console.log('  └─ Total tasks to export:', tasksToExport.length);
        console.log('  └─ Data source:', filteredUserApiTasks.length > 0 ? 'filteredUserApiTasks' : 'filteredTasks');
        console.log('  └─ Export filename:', filename);
        console.log('');
        console.log('📋 SAMPLE TASKS (first 3):');
        tasksToExport.slice(0, 3).forEach((t, idx) => {
            console.log(`  ${idx + 1}. Task #${t.taskId}:`);
            console.log(`     └─ Title: ${t.title}`);
            console.log(`     └─ Created: ${t.createdAt}`);
            console.log(`     └─ Status: ${t.status}`);
            console.log(`     └─ Priority: ${t.priority}`);
            console.log(`     └─ Assignee: ${t.assigneeName}`);
            console.log(`     └─ Project: ${t.projectName || 'N/A'}`);
        });
        console.log('');
        console.log('════════════════════════════════════════════════════════════');

        // Generate CSV
        const headers = ['Serial No', 'Task No', 'Day', 'Report Date', 'TASK OPEN DT', 'PROJECT', 'CUSTOMER', 'Task Subject', 'Category', 'PRIORITY', 'Assignee Name', 'FOLLOW UP BY', 'Action & Task Status', 'TASK END DT', 'CURRENT STATUS', 'Days Over', 'Comment Review Dt', 'Remarks/Comments & Updates'];
        const formatValue = v => v === null || v === undefined ? '' : (/[",\n]/.test(String(v)) ? `"${String(v).replace(/"/g, '""')}"` : String(v));
        const formatDate = d => d ? new Date(d).toLocaleDateString('en-GB') : '';
        const getDay = d => d ? new Date(d).toLocaleString('en-us', { weekday: 'short' }) : '';
        const daysOver = t => String(t.status).toLowerCase().includes('completed') || !t.dueDate ? 0 : Math.max(0, Math.ceil((new Date() - new Date(t.dueDate)) / (1000 * 60 * 60 * 24)));
        const getComments = t => [...(t.comments || []), ...(t.remarks || [])].filter(Boolean).join(" | ");
        
        const csvRows = [
            headers.join(','), 
            ...tasksToExport.map((t, i) => [
                i + 1, 
                formatValue(t.taskId), 
                getDay(t.createdAt), 
                formatDate(t.createdAt), 
                formatDate(t.createdAt), 
                formatValue(t.projectName), 
                formatValue(t.customerName), 
                formatValue(t.title), 
                formatValue(t.category), 
                formatValue(t.priority), 
                formatValue(t.assigneeName), 
                formatValue(t.assigneeName), 
                formatValue(t.description), 
                formatDate(t.dueDate), 
                formatValue(statusLabels[t.status] || t.status), 
                daysOver(t), 
                '', 
                formatValue(getComments(t))
            ].join(','))
        ];
        
        const csvString = csvRows.join('\n');
        const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Show comprehensive success message
        const activeFilters = [];
        if (selectedYears.length > 0) activeFilters.push(`Year: ${selectedYearData.map(y => y.label).join(', ')}`);
        if (selectedMonths.length > 0) {
            const monthLabels = masterMonths.filter(m => selectedMonths.includes(m.id)).map(m => m.label || m.name).join(', ');
            activeFilters.push(`Month: ${monthLabels}`);
        }
        if (selectedStatuses.length > 0) activeFilters.push(`Status: ${selectedStatuses.length} selected`);
        if (selectedPriorities.length > 0) activeFilters.push(`Priority: ${selectedPriorities.length} selected`);
        if (selectedProjects.length > 0) activeFilters.push(`Project: ${selectedProjects.length} selected`);
        if (selectedCategories.length > 0) activeFilters.push(`Category: ${selectedCategories.length} selected`);
        if (selectedUsers.length > 0) activeFilters.push(`User: ${selectedUsers.length} selected`);
        if (selectedDepartments.length > 0) activeFilters.push(`Department: ${selectedDepartments.length} selected`);
        if (selectedCustomers.length > 0 || selectedSuppliers.length > 0) {
            activeFilters.push(`Client: ${selectedCustomers.length + selectedSuppliers.length} selected`);
        }
        
        const filterSummary = activeFilters.length > 0 
            ? `\n\nActive Filters:\n${activeFilters.join('\n')}`
            : '\n\nNo filters applied (full export)';
        
        alert(`✅ Export Successful!\n\nExported ${tasksToExport.length} tasks${filterSummary}\n\nFile: ${filename}`);
    };

    const getStatusInfo = (statusKey) => {
        const statusLabel = statusLabels[statusKey] || statusKey.charAt(0).toUpperCase() + statusKey.slice(1);
        const lower = statusLabel.toLowerCase();
        const colors = { color: '#6B7280', bgColor: '#F3F4F6', iconColor: '#6B7280' };
        if (lower.includes('completed')) { colors.color = '#10B981'; colors.bgColor = '#ECFDF5'; colors.iconColor = '#10B981'; }
        else if (lower.includes('review')) { colors.color = '#F59E0B'; colors.bgColor = '#FFFBEB'; colors.iconColor = '#F59E0B'; }
        else if (lower.includes('follow')) { colors.color = '#8B5CF6'; colors.bgColor = '#F3E8FF'; colors.iconColor = '#8B5CF6'; }
        else if (lower.includes('open')) { colors.color = '#3B82F6'; colors.bgColor = '#EFF6FF'; colors.iconColor = '#3B82F6'; }
        else if (lower.includes('wip')) { colors.color = '#EC4899'; colors.bgColor = '#FDF2F8'; colors.iconColor = '#EC4899'; }
        return { label: statusLabel, ...colors, icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={colors.iconColor} strokeWidth="2"><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>, };
    };

    // --- Totals for Stat Cards (calculated from filtered tasks to respect Month/Year) ---
    const computedTaskStats = useMemo(() => {
        const stats = {
            total: 0,
            completed: 0,
            wip: 0,
            underreview: 0,
            followup: 0,
            open: 0,
        };

        filteredUserApiTasks.forEach(task => {
            stats.total++;
            const lowerStatus = String(task.status || '').toLowerCase();
            if (lowerStatus.includes('completed')) stats.completed++;
            else if (lowerStatus.includes('wip') || lowerStatus.includes('progress')) stats.wip++;
            else if (lowerStatus.includes('review')) stats.underreview++;
            else if (lowerStatus.includes('follow')) stats.followup++;
            else if (lowerStatus.includes('open')) stats.open++;
        });

        return stats;
    }, [filteredUserApiTasks]);

    const renderTaskStatusTreeContent = (onTaskClick) => {
        // If loading specifically for this section
        if (isLoadingUserApiTasks) return <div className="team-status-message">Loading Status Data...</div>;

        const statusGroups = {};
        // Create buckets based on known status labels (e.g., 'underreview', 'followup')
        Object.keys(statusLabels).forEach(key => statusGroups[key] = []);

        // Distribute tasks into buckets with robust matching
        filteredUserApiTasks.forEach(task => {
            if (!task.status) return;

            // 1. Try exact match (rare if keys are normalized)
            if (statusGroups[task.status]) {
                statusGroups[task.status].push(task);
                return;
            }

            // 2. Normalize task status to match keys (remove non-alphanumeric, lowercase)
            const normalizedTaskStatus = String(task.status).toLowerCase().replace(/[^a-z0-9]/g, '');
            if (statusGroups[normalizedTaskStatus]) {
                statusGroups[normalizedTaskStatus].push(task);
                return;
            }

            // 3. Fallback/Fuzzy matching
            if (normalizedTaskStatus.includes('review') && statusGroups['underreview']) {
                statusGroups['underreview'].push(task);
            } else if (normalizedTaskStatus.includes('follow') && statusGroups['followup']) {
                statusGroups['followup'].push(task);
            } else if ((normalizedTaskStatus.includes('wip') || normalizedTaskStatus.includes('progress')) && statusGroups['wip']) {
                statusGroups['wip'].push(task);
            } else if (normalizedTaskStatus.includes('open') && statusGroups['open']) {
                statusGroups['open'].push(task);
            } else if (normalizedTaskStatus.includes('complete') && statusGroups['completed']) {
                statusGroups['completed'].push(task);
            }
        });

        const statusTreeData = Object.entries(statusGroups).map(([statusKey, tasks], index) => {
            const statusInfo = getStatusInfo(statusKey);
            return { id: `status-${index}`, statusKey, name: statusInfo.label, designation: `Tasks: ${tasks.length}`, bgColor: statusInfo.bgColor, color: statusInfo.color, tasks, children: [], };
        }).filter(group => group.tasks.length > 0);

        if (!statusTreeData.length) return <div className="team-status-message">No tasks available for the current filter.</div>;

        const midIndex = Math.ceil(statusTreeData.length / 2);
        const leftColumnData = statusTreeData.slice(0, midIndex);
        const rightColumnData = statusTreeData.slice(midIndex);

        return {
            leftColumn: (<div className="team-tree-wrapper"><ul className="team-tree">{leftColumnData.map((node) => (<TreeNode key={node.id} node={node} onClick={() => handleNavigateToTasks({ status: statusKeyToFilterValue[node.statusKey] })} onTaskClick={onTaskClick} />))}</ul></div>),
            rightColumn: (<div className="team-tree-wrapper"><ul className="team-tree">{rightColumnData.map((node) => (<TreeNode key={node.id} node={node} onClick={() => handleNavigateToTasks({ status: statusKeyToFilterValue[node.statusKey] })} onTaskClick={onTaskClick} />))}</ul></div>),
        };
    };

    const renderTreeContent = (onTaskClick) => {
        if (isTreeLoading) return <div className="team-status-message">Loading Organization Tree...</div>;
        if (treeError) return <div className="team-status-message error">{`Error: ${treeError}`}</div>;

        if (!treeForDisplay.length) return <div className="team-status-message">No hierarchy data found or matches the current filter.</div>;

        return (<div className="team-tree-wrapper"><ul className="team-tree">{treeForDisplay.map((node) => <TreeNode key={node.id} node={node} onTaskClick={onTaskClick} />)}</ul></div>);
    };

    const handleToggleProject = (projectId) => {
        const isCurrentlyExpanded = expandedProjects.has(projectId);

        setExpandedProjects(prev => {
            const newSet = new Set(prev);
            if (isCurrentlyExpanded) {
                newSet.delete(projectId);
            } else {
                newSet.add(projectId);
            }
            return newSet;
        });

        if (isCurrentlyExpanded) {
            const projectNameKey = projectId.replace('project-', '');
            const taskIdsToCollapse = filteredTasks
                .filter(t => (t.projectName || "No project assigned") === projectNameKey)
                .map(t => `task-${t.taskId}`);

            setExpandedTasks(prev => {
                const newSet = new Set(prev);
                taskIdsToCollapse.forEach(id => newSet.delete(id));
                return newSet;
            });
        }
    };

    // --- Async handler to fetch subtasks on demand ---
    const handleToggleTask = useCallback(async (ganttTaskId, hasSubtasks) => {
        const parentTaskId = parseInt(ganttTaskId.replace('task-', ''), 10);
        if (isNaN(parentTaskId)) return;

        const isExpanding = !expandedTasks.has(ganttTaskId);

        setExpandedTasks(prev => {
            const newSet = new Set(prev);
            isExpanding ? newSet.add(ganttTaskId) : newSet.delete(ganttTaskId);
            return newSet;
        });

        if (isExpanding && hasSubtasks && !subtaskCache[parentTaskId] && !loadingSubtasks.has(parentTaskId)) {
            try {
                setLoadingSubtasks(prev => new Set(prev).add(parentTaskId));

                const response = await fetch(`${SWAGGER_BASE_URL}/api/get-sub-tasks?parentTaskId=${parentTaskId}`, {
                    headers: { accept: 'application/json', Authorization: API_TOKEN }
                });

                if (!response.ok) throw new Error(`Failed to fetch subtasks: ${response.status}`);

                const result = await response.json();
                const fetchedSubtasks = result.data || [];

                setSubtaskCache(prev => ({ ...prev, [parentTaskId]: fetchedSubtasks }));

            } catch (error) {
                console.error(`Error fetching subtasks for task ${parentTaskId}:`, error);
                setExpandedTasks(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(ganttTaskId);
                    return newSet;
                });
            } finally {
                setLoadingSubtasks(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(parentTaskId);
                    return newSet;
                });
            }
        }
    }, [expandedTasks, subtaskCache, loadingSubtasks, API_TOKEN]);


    const handleDownloadPdf = async () => {
        setIsGeneratingPdf(true);
        if (!ganttChartRef.current) { alert("Gantt chart not rendered."); setIsGeneratingPdf(false); return; }
        try {
            const canvas = await html2canvas(ganttChartRef.current, { scale: 2 });
            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF('landscape', 'pt', 'a4');
            const imgProps = pdf.getImageProperties(imgData);
            const pdfWidth = pdf.internal.pageSize.getWidth() - 40;
            const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
            pdf.text("Gantt Chart Export", 20, 30);
            pdf.addImage(imgData, 'PNG', 20, 50, pdfWidth, pdfHeight);
            pdf.save("gantt_chart_dashboard.pdf");
        } catch (error) {
            console.error("Error generating PDF:", error); alert("Failed to generate PDF.");
        } finally {
            setIsGeneratingPdf(false);
        }
    };

    if (isDashboardLoading) {
        return (<div className="loading-container"><ArrowPathIcon className="loading-icon" /><p className="loading-text">Loading Dashboard...</p></div>);
    }

    const statusTreeContent = renderTaskStatusTreeContent(handleTaskClick);

    const handleCellClick = (e, filter) => { e.stopPropagation(); handleNavigateToTasks(filter); };

    return (
        <div className="dashboard-container">
            <div className="filter-container">
                <MultiSelectDropdown defaultLabel="All Users" value={selectedUsers} onChange={handleUsersChange} options={dropdownOptions.userOptions} icon={<IoPeopleOutline className="filter-icon" />} />
                <MultiSelectDropdown defaultLabel="All Departments" value={selectedDepartments} onChange={handleDepartmentsChange} options={dropdownOptions.departmentOptions} icon={<IoBusinessOutline className="filter-icon" />} />
                <MultiSelectDropdown defaultLabel="All Vendor Types" value={selectedVendorTypes} onChange={handleVendorTypesChange} options={dropdownOptions.vendorTypeOptions} icon={<IoPricetagOutline className="filter-icon" />} />

                {customerVendorTypeId && selectedVendorTypes.includes(String(customerVendorTypeId)) && (
                    <MultiSelectDropdown defaultLabel="All Customers" value={selectedCustomers} onChange={handleCustomersChange} options={dropdownOptions.customerOptions} icon={<IoBusinessOutline className="filter-icon" />} displayAllSelectedLabels={true} />
                )}
                {supplierVendorTypeId && selectedVendorTypes.includes(String(supplierVendorTypeId)) && (
                    <MultiSelectDropdown defaultLabel="All Suppliers" value={selectedSuppliers} onChange={handleSuppliersChange} options={dropdownOptions.supplierOptions} icon={<IoPeopleOutline className="filter-icon" />} />
                )}

                <MultiSelectDropdown defaultLabel="All Years" value={selectedYears} onChange={handleYearsChange} options={dropdownOptions.yearOptions} icon={<IoCalendarOutline className="filter-icon" />} />
                <MultiSelectDropdown defaultLabel="All Months" value={selectedMonths} onChange={handleMonthsChange} options={dropdownOptions.monthOptions} icon={<IoCalendarOutline className="filter-icon" />} />
                <MultiSelectDropdown defaultLabel="All Projects" value={selectedProjects} onChange={handleProjectsChange} options={dropdownOptions.projectOptions} icon={<IoBriefcaseOutline className="filter-icon" />} />
                <MultiSelectDropdown defaultLabel="All Categories" value={selectedCategories} onChange={handleCategoriesChange} options={dropdownOptions.categoryOptions} icon={<IoPricetagOutline className="filter-icon" />} />
                <MultiSelectDropdown defaultLabel="All Statuses" value={selectedStatuses} onChange={handleStatusChange} options={dropdownOptions.statusOptions} icon={<IoCheckmarkCircleOutline className="filter-icon" />} />
                <MultiSelectDropdown defaultLabel="All Priorities" value={selectedPriorities} onChange={handlePriorityChange} options={dropdownOptions.priorityOptions} icon={<IoFlagOutline className="filter-icon" />} />
                <div className="filter-actions">
                    <button onClick={handleExportCSV} className="export-button"><IoDownloadOutline className="export-icon" /> Export</button>
                    <button onClick={handleResetFilters} className="reset-button"><IoRefresh className="reset-icon" /> Reset</button>
                </div>
            </div>

            {dashboardError && <div className="error-message">Error: {dashboardError}</div>}

            <div className="stats-container">
                {Object.entries(computedTaskStats).map(([key, value]) => {
                    const statusInfo = key === 'total' ? { label: 'Total Tasks', icon: <svg width="24" height="24" viewBox="0 0 24" fill="none" stroke="#4B5EAA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path><rect x="8" y="2" width="8" height="4" rx="1" ry="1"></rect></svg>, iconColor: '#4B5EAA' } : getStatusInfo(key);
                    const isClickable = value > 0;
                    const totalTasks = computedTaskStats.total;
                    const percentage = totalTasks > 0 ? ((value / totalTasks) * 100).toFixed(2) : '0.00';

                    // --- CHANGED: Calculate filters to pass on click ---
                    const dashboardFilters = getDashboardDrillDownFilters();
                    const navigateFilters = { ...dashboardFilters };
                    if (key !== 'total') {
                        navigateFilters.status = statusKeyToFilterValue[key];
                    }

                    return (
                        <div key={key} className={`stat-card ${isClickable ? 'clickable' : ''}`} onClick={isClickable ? () => handleNavigateToTasks(navigateFilters) : undefined}>
                            <div className="stat-icon" style={{ color: statusInfo.iconColor }}>{statusInfo.icon}</div>
                            <div><div className="stat-value">{value} {totalTasks > 0 && key !== 'total' && <span style={{ fontSize: '0.6em', marginLeft: '4px', verticalAlign: 'middle' }}>({percentage}%)</span>}</div><div className="stat-label">{statusInfo.label}</div></div>
                        </div>
                    );
                })}
            </div>

            {/* --- User Wise Summary --- */}
            <div className="summary-container">
                <h2 className="summary-title">User Wise Summary {isLoadingUserApiTasks && <span style={{ fontSize: '0.8em', fontWeight: 'normal', marginLeft: '10px' }}>(Updating...)</span>}</h2>
                {userWiseTaskStats.length > 0 ? (
                    <div className="table-wrapper"><table className="summary-table">
                        <thead><tr>{['User', 'Total Tasks', 'Completed', 'WIP', 'Under Review', 'Follow-up', 'Open'].map(h => <th key={h} className="table-header text-center">{h}</th>)}</tr></thead>
                        <tbody>
                            {userWiseTaskStats.map((item, index) => (
                                <tr key={item.id || item.name + index}>
                                    <td className={`table-cell user-cell ${item.total > 0 ? 'clickable-cell' : ''}`} onClick={item.total > 0 ? () => handleNavigateToTasks({ userId: String(item.id) }) : undefined}>{item.name}</td>
                                    <td className={`table-cell total text-center ${item.total > 0 ? 'clickable-cell' : ''}`} onClick={item.total > 0 ? () => handleNavigateToTasks({ userId: String(item.id) }) : undefined}>{item.total}</td>
                                    <td className={`table-cell completed text-center ${item.completed > 0 ? 'clickable-cell' : ''}`} onClick={item.completed > 0 ? e => handleCellClick(e, { userId: String(item.id), status: statusKeyToFilterValue.completed }) : undefined}>{item.completed} {item.total > 0 ? `(${((item.completed / item.total) * 100).toFixed(2)}%)` : ''}</td>
                                    <td className={`table-cell wip text-center ${item.wip > 0 ? 'clickable-cell' : ''}`} onClick={item.wip > 0 ? e => handleCellClick(e, { userId: String(item.id), status: statusKeyToFilterValue.wip }) : undefined}>{item.wip} {item.total > 0 ? `(${((item.wip / item.total) * 100).toFixed(2)}%)` : ''}</td>
                                    <td className={`table-cell underreview text-center ${item.underreview > 0 ? 'clickable-cell' : ''}`} onClick={item.underreview > 0 ? e => handleCellClick(e, { userId: String(item.id), status: statusKeyToFilterValue.underreview }) : undefined}>{item.underreview} {item.total > 0 ? `(${((item.underreview / item.total) * 100).toFixed(2)}%)` : ''}</td>
                                    <td className={`table-cell followup text-center ${item.followup > 0 ? 'clickable-cell' : ''}`} onClick={item.followup > 0 ? e => handleCellClick(e, { userId: String(item.id), status: statusKeyToFilterValue.followup }) : undefined}>{item.followup} {item.total > 0 ? `(${((item.followup / item.total) * 100).toFixed(2)}%)` : ''}</td>
                                    <td className={`table-cell open text-center ${item.open > 0 ? 'clickable-cell' : ''}`} onClick={item.open > 0 ? e => handleCellClick(e, { userId: String(item.id), status: statusKeyToFilterValue.open }) : undefined}>{item.open} {item.total > 0 ? `(${((item.open / item.total) * 100).toFixed(2)}%)` : ''}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table></div>) : (<div className="no-data">No user data for selected filters.</div>)
                }
            </div>

            {/* --- Project Wise Summary --- */}
            <div className="summary-container">
                <h2 className="summary-title">Project Wise Summary {isLoadingUserApiTasks && <span style={{ fontSize: '0.8em', fontWeight: 'normal', marginLeft: '10px' }}>(Updating...)</span>}</h2>
                {projectTaskStats.length > 0 ? (
                    <div className="table-wrapper"><table className="summary-table">
                        <thead><tr>{['Project', 'Total Tasks', 'Completed', 'WIP', 'Under Review', 'Follow-up', 'Open'].map(h => <th key={h} className="table-header text-center">{h}</th>)}</tr></thead>
                        <tbody>
                            {projectTaskStats.map((item, index) => (
                                <tr key={item.id || item.name + index}>
                                    <td className={`table-cell user-cell ${item.total > 0 ? 'clickable-cell' : ''}`} onClick={item.total > 0 ? () => handleNavigateToTasks({ projectName: item.name.replace(/ /g, '_') }) : undefined}>{item.name}</td>
                                    <td className={`table-cell total text-center ${item.total > 0 ? 'clickable-cell' : ''}`} onClick={item.total > 0 ? () => handleNavigateToTasks({ projectName: item.name.replace(/ /g, '_') }) : undefined}>{item.total}</td>
                                    <td className={`table-cell completed text-center ${item.completed > 0 ? 'clickable-cell' : ''}`} onClick={item.completed > 0 ? e => handleCellClick(e, { projectName: item.name.replace(/ /g, '_'), status: statusKeyToFilterValue.completed }) : undefined}>{item.completed} {item.total > 0 ? `(${((item.completed / item.total) * 100).toFixed(2)}%)` : ''}</td>
                                    <td className={`table-cell wip text-center ${item.wip > 0 ? 'clickable-cell' : ''}`} onClick={item.wip > 0 ? e => handleCellClick(e, { projectName: item.name.replace(/ /g, '_'), status: statusKeyToFilterValue.wip }) : undefined}>{item.wip} {item.total > 0 ? `(${((item.wip / item.total) * 100).toFixed(2)}%)` : ''}</td>
                                    <td className={`table-cell underreview text-center ${item.underreview > 0 ? 'clickable-cell' : ''}`} onClick={item.underreview > 0 ? e => handleCellClick(e, { projectName: item.name.replace(/ /g, '_'), status: statusKeyToFilterValue.underreview }) : undefined}>{item.underreview} {item.total > 0 ? `(${((item.underreview / item.total) * 100).toFixed(2)}%)` : ''}</td>
                                    <td className={`table-cell followup text-center ${item.followup > 0 ? 'clickable-cell' : ''}`} onClick={item.followup > 0 ? e => handleCellClick(e, { projectName: item.name.replace(/ /g, '_'), status: statusKeyToFilterValue.followup }) : undefined}>{item.followup} {item.total > 0 ? `(${((item.followup / item.total) * 100).toFixed(2)}%)` : ''}</td>
                                    <td className={`table-cell open text-center ${item.open > 0 ? 'clickable-cell' : ''}`} onClick={item.open > 0 ? e => handleCellClick(e, { projectName: item.name.replace(/ /g, '_'), status: statusKeyToFilterValue.open }) : undefined}>{item.open} {item.total > 0 ? `(${((item.open / item.total) * 100).toFixed(2)}%)` : ''}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table></div>) : (<div className="no-data">No project data for selected filters.</div>)
                }
            </div>

            <div className="gantt-chart-section">
                <h2 style={{ marginBottom: '1rem' }}>Project Gantt Chart</h2>
                <div className="gantt-controls">
                    <strong>Zoom:</strong>
                    <button onClick={() => setGanttViewMode(ViewMode.Day)} className={`gantt-view-btn ${ganttViewMode === ViewMode.Day && 'active'}`}>Day</button>
                    <button onClick={() => setGanttViewMode(ViewMode.Week)} className={`gantt-view-btn ${ganttViewMode === ViewMode.Week && 'active'}`}>Week</button>
                    <button onClick={() => setGanttViewMode(ViewMode.Month)} className={`gantt-view-btn ${ganttViewMode === ViewMode.Month && 'active'}`}>Month</button>
                    <button onClick={handleDownloadPdf} className="gantt-view-btn" disabled={isGeneratingPdf}>{isGeneratingPdf ? 'Generating...' : 'Download PDF'}</button>
                </div>
                <div ref={ganttChartRef} className="gantt-chart-wrapper">
                    {ganttTasks.length === 0 ? (<p>No data for Gantt chart with current filters.</p>) : (
                        <Gantt
                            key={ganttKey}
                            tasks={ganttTasks}
                            viewMode={ganttViewMode}
                            listCellWidth="350px"
                            ganttHeight={450}
                            columnWidth={65}
                            TaskListHeader={CustomTaskListHeader}
                            TaskListTable={(props) => <CustomTaskListTable {...props} tasks={ganttTasks} onToggleProject={handleToggleProject} expandedProjects={expandedProjects} onToggleTask={handleToggleTask} expandedTasks={expandedTasks} />}
                            TooltipContent={({ task }) => (<div className="gantt-tooltip"> <div><strong>{task.displayName}</strong></div> {task.type === 'project' && <div>Progress: {task.progress}%</div>} <div>Duration: {formatTotalDuration(task.start, task.end)}</div> <div>Start: {formatDateDDMMYY(task.start)}</div> <div>End: {formatDateDDMMYY(task.end)}</div> </div>)}
                        />
                    )}
                </div>
            </div>

            {/* --- Category Wise Summary --- */}
            <div className="summary-container">
                <h2 className="summary-title">Category Wise Summary {isLoadingUserApiTasks && <span style={{ fontSize: '0.8em', fontWeight: 'normal', marginLeft: '10px' }}>(Updating...)</span>}</h2>
                {categoryTaskStats.length > 0 ? (
                    <div className="table-wrapper"><table className="summary-table">
                        <thead><tr>{['Category', 'Total Tasks', 'Completed', 'WIP', 'Under Review', 'Follow-up', 'Open'].map(h => <th key={h} className="table-header text-center">{h}</th>)}</tr></thead>
                        <tbody>
                            {categoryTaskStats.map((item, index) => (
                                <tr key={item.id || item.name + index}>
                                    <td className={`table-cell user-cell ${item.total > 0 ? 'clickable-cell' : ''}`} onClick={item.total > 0 ? () => handleNavigateToTasks({ category: item.name.replace(/ /g, '_') }) : undefined}>{item.name}</td>
                                    <td className={`table-cell total text-center ${item.total > 0 ? 'clickable-cell' : ''}`} onClick={item.total > 0 ? () => handleNavigateToTasks({ category: item.name.replace(/ /g, '_') }) : undefined}>{item.total}</td>
                                    <td className={`table-cell completed text-center ${item.completed > 0 ? 'clickable-cell' : ''}`} onClick={item.completed > 0 ? e => handleCellClick(e, { category: item.name.replace(/ /g, '_'), status: statusKeyToFilterValue.completed }) : undefined}>{item.completed} {item.total > 0 ? `(${((item.completed / item.total) * 100).toFixed(2)}%)` : ''}</td>
                                    <td className={`table-cell wip text-center ${item.wip > 0 ? 'clickable-cell' : ''}`} onClick={item.wip > 0 ? e => handleCellClick(e, { category: item.name.replace(/ /g, '_'), status: statusKeyToFilterValue.wip }) : undefined}>{item.wip} {item.total > 0 ? `(${((item.wip / item.total) * 100).toFixed(2)}%)` : ''}</td>
                                    <td className={`table-cell underreview text-center ${item.underreview > 0 ? 'clickable-cell' : ''}`} onClick={item.underreview > 0 ? e => handleCellClick(e, { category: item.name.replace(/ /g, '_'), status: statusKeyToFilterValue.underreview }) : undefined}>{item.underreview} {item.total > 0 ? `(${((item.underreview / item.total) * 100).toFixed(2)}%)` : ''}</td>
                                    <td className={`table-cell followup text-center ${item.followup > 0 ? 'clickable-cell' : ''}`} onClick={item.followup > 0 ? e => handleCellClick(e, { category: item.name.replace(/ /g, '_'), status: statusKeyToFilterValue.followup }) : undefined}>{item.followup} {item.total > 0 ? `(${((item.followup / item.total) * 100).toFixed(2)}%)` : ''}</td>
                                    <td className={`table-cell open text-center ${item.open > 0 ? 'clickable-cell' : ''}`} onClick={item.open > 0 ? e => handleCellClick(e, { category: item.name.replace(/ /g, '_'), status: statusKeyToFilterValue.open }) : undefined}>{item.open} {item.total > 0 ? `(${((item.open / item.total) * 100).toFixed(2)}%)` : ''}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table></div>) : (<div className="no-data">No category data for selected filters.</div>)
                }
            </div>

            <div className="team-page-container">
                <header className="team-header"><div className="header-content"><h1>Task Overview by Status</h1></div></header>
                <div className="status-tree-container">
                    <div className="status-tree-column">{statusTreeContent.leftColumn}</div>
                    <div className="status-tree-column">{statusTreeContent.rightColumn}</div>
                </div>
            </div>
            <div className="team-page-container">
                <header className="team-header"><h1>Your Team Structure</h1></header>
                {renderTreeContent(handleTaskClick)}
            </div>
        </div>
    );
};

export default Dashboard;